import { useWorkspaces, Workspace } from '@/contexts/AuthContext/useWorkspaces';
import { useDialog } from '@/contexts/DialogContext';
import {
    AUTH_STORAGE_KEY,
    authService, supabase, VerifyAuthResponse
} from '@common/api/authService';
import { User } from '@supabase/supabase-js';
// 重新导出工具函数
export { getMemberAvatarUrl, getUserAvatarUrl } from './utils';
export { useOauth } from './useOauth';
//@ts-ignore
import Cookies from 'js-cookie';
import React, {
    createContext,
    useContext, useEffect,
    useState
} from 'react';
import { useNavigate } from 'react-router-dom';
// import { NangoIntegrations, useNango } from './useNango'; // Deprecated - 已整合到 useWorkspaceIntegrations
import { useUserStorage } from '@common/hooks/useUserStorage';
import GlobalLoading from '../../components/GlobalLoading';
import { usePermissions, UsePermissionsReturn } from './usePermissions';
import { SyncStatusState, useSyncStatus } from './useSyncStatus';
import { UserBilling, useUserBilling } from './useUserBilling';
import { useWorkspaceIntegrations, UseWorkspaceIntegrationsReturn } from './useWorkspaceIntegrations';
import type {
    MemberStatus,
    UseWorkspaceMembersReturn,
    WorkspaceInvitation,
    WorkspaceMemberDisplay
} from './useWorkspaceMembers';
import { useWorkspaceMembers } from './useWorkspaceMembers';
import type { WorkspaceRole } from './useWorkspaces';
import { useOauth } from './useOauth';

// 重新导出常用类型
export type {
    MemberStatus, WorkspaceInvitation, WorkspaceMemberDisplay, WorkspaceRole
};

// 重新导出权限相关类型和枚举
    export type {
        RolePermission,
        UsePermissionsReturn
    } from './usePermissions';

export { Permission } from './permissions';


declare global {
    interface Window {
        isShopify: boolean;
    }
}

interface AuthContextType {
    openSignInModal: () => void;
    logout: () => void,
    user: User | null;
    profile: VerifyAuthResponse | null;
    setProfile: (profile: VerifyAuthResponse | null) => void;
    userBilling: UserBilling;
    integrations: UseWorkspaceIntegrationsReturn;
    workspaceMembers: UseWorkspaceMembersReturn;
    syncStatus: SyncStatusState;
    permissions: UsePermissionsReturn;
    isLoadingProfile: boolean;
    workspaces: Workspace[];
    currentWorkspace: Workspace | null;
    isLoadingWorkspaces: boolean;
    refreshWorkspaces: (options?: { silent?: boolean }) => Promise<void>;
    switchWorkspace: (workspaceId: string, params?: Record<string, string>) => void;
    isGlobalLoading: boolean;
    showGlobalLoading: () => void;
    hideGlobalLoading: () => void;
}

// 创建一个默认值
const defaultContextValue: AuthContextType = {
    openSignInModal: () => { },
    logout: () => { },
    user: null,
    profile: null,
    setProfile: () => {},
    isLoadingProfile: false,
    workspaces: [],
    currentWorkspace: null,
    isLoadingWorkspaces: false,
    refreshWorkspaces: async (_options?: { silent?: boolean }) => {},
    switchWorkspace: () => {},
    isGlobalLoading: false,
    showGlobalLoading: () => {},
    hideGlobalLoading: () => {},
    userBilling: {
        userPlan: null,
        userPoints: null,
        pointsHistory: {
            pagination: {
                total: 0,
                size: 0,
                totalPages: 0,
                hasPrevious: false,
                hasNext: false,
                page: 0
            },
            data: []
        },
        invoices: {
            pagination: {
                total: 0,
                size: 0,
                totalPages: 0,
                hasPrevious: false,
                hasNext: false,
                page: 0
            },
            invoices: []
        },
        loading: {
            plan: false,
            points: false,
            history: false,
            invoices: false
        },
        error: {
            plan: null,
            points: null,
            history: null,
            invoices: null
        },
        fetchUserPoints: async () => Promise.resolve(),
        fetchPointsHistory: async () => Promise.resolve(),
        fetchInvoices: async () => Promise.resolve(),
        refreshAll: async () => Promise.resolve(),
        planText: {
            planDisplayText: '',
            badge: '',
            expirationText: '',
            buttonText: '',
            status: 'none'
        },

        isExpired: false,
        isCanceled: false,
        isTrial: false,
    },
    integrations: {} as any,
    workspaceMembers: {
        members: [],
        invitations: [],
        pendingInvites: [],
        isLoading: false,
        isLoadingInvitations: false,
        error: null,
        refresh: async () => {},
        inviteMember: async () => {},
        removeMember: async () => {},
        updateMemberRole: async () => {},
        currentUserRole: null,
        canManageMembers: false,
    },
    syncStatus: {
        syncStatuses: [],
        loading: false,
        syncCompleted: false
    },
    permissions: {
        isLoading: false,
        error: null,
        checkPermissions: () => false,
        refresh: async () => {},
    },
    // shopifyEmbed: {
    //     isEmbedded: false,
    //     isAppBridgeReady: false,
    //     shopify: null,
    //     shopDomain: null,
    //     redirectToShopify: () => {}
    // }
};

// 使用默认值创建 Context
export const AuthContext = createContext<AuthContextType>(defaultContextValue);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [profile, setProfile] = useState<VerifyAuthResponse | null>(null);
    const [isGlobalLoading, setIsGlobalLoading] = useState(true);
    const userBilling = useUserBilling(profile);
    const {set} = useUserStorage();
    const { getOAuthContext } = useOauth();

    const navigate = useNavigate();

    const showGlobalLoading = () => setIsGlobalLoading(true);
    const hideGlobalLoading = () => setIsGlobalLoading(false);

    const openSignInModal = () => {
        navigate(`/auth?redirect=${window.location.href}`);
    };

    const { showDialog } = useDialog();

    const _logout = async () => {
        Cookies.remove(AUTH_STORAGE_KEY);
        try {
            const result = await supabase.auth.signOut();
            if(result.error){
                throw result.error;
            }else{
                localStorage.removeItem(AUTH_STORAGE_KEY);
                window.location.href = '/'
            }
        } catch (error) {
            console.error('Logout failed:', error);
            localStorage.removeItem(AUTH_STORAGE_KEY);
            window.location.href = '/';
        }
    };

    const logout = async () => {
        // Show a confirmation dialog
        const confirmed = await showDialog({
            title: 'Are you sure to log out?',
            subtitle: `Log out of ${user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'your account'}`,
            confirmText: 'Log out',
            cancelText: 'Stay logged in',
        });

        if (confirmed) {
            _logout();
        }
    }

    useEffect(() => {
        const init = async () => {
            // 检测 URL 中的错误参数（Supabase 认证错误）
            // 检查 query string 和 hash fragment
            const searchParams = new URLSearchParams(window.location.search);
            const hashParams = new URLSearchParams(window.location.hash.split('#')[1] || '');

            const errorCode = searchParams.get('error_code') || hashParams.get('error_code');
            const error = searchParams.get('error') || hashParams.get('error');
            const errorDesc = searchParams.get('error_description') || hashParams.get('error_description');

            // 如果有错误参数且不在错误页面，重定向到错误页面
            if ((errorCode || error || errorDesc) && !window.location.pathname.includes('/auth/error')) {
                const errorParams = new URLSearchParams();
                if (errorCode) errorParams.set('error_code', errorCode);
                if (error) errorParams.set('error', error);
                if (errorDesc) errorParams.set('error_description', errorDesc);

                window.location.href = `/admin/auth/error?${errorParams.toString()}`;
                return;
            }

            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                try {
                    const verifyAuth = await authService.verifyAuth();
                    userBilling.refreshAll();
                    console.log("verifyAuth", verifyAuth);
                    setProfile(verifyAuth);
                    Cookies.set(AUTH_STORAGE_KEY, session.access_token, {
                        path: '/',        // 全站有效
                        sameSite: 'lax',  // 或 'none' + secure
                        secure: true      // HTTPS 必须
                    })

                    // 调用 OAuth 授权接口（异步方法）
                    const workspaceId = verifyAuth.workspace?.id || (window as any).Bustly?.workspace_id;
                    if (workspaceId && getOAuthContext()) {
                        try {
                            await authService.getOAuthAuthorize(workspaceId);
                        } catch (oauthError) {
                            console.error('[AuthContext] OAuth 授权请求失败:', oauthError);
                        }
                    } else {
                        console.log('[AuthContext] 跳过 OAuth 授权请求：workspace_id 不存在或无 oauth_context');
                    }
                } catch (error: any) {
                    if(error.message === '401'){
                        logout();
                        openSignInModal();
                    }
                    if(error.message === '1003'){
                        set('currentWorkspaceId', '')
                        window.location.reload();
                    }

                    console.error('Verify auth failed:', error.message);
                }finally{
                    setIsLoadingProfile(false);
                    hideGlobalLoading();
                }
            }else{
                // 未登录且不在 /auth 页面，跳转到登录页
                const currentPath = window.location.pathname;
                if (!currentPath.startsWith('/auth')) {
                    const redirectUrl = window.location.href;
                    navigate(`/auth?redirect=${encodeURIComponent(redirectUrl)}`);
                }
                setIsLoadingProfile(false);
                hideGlobalLoading();
            }
        }

        init();
    }, [])

    // 监听 Supabase 的 auth 状态变化
    useEffect(() => {
        // 监听 auth 状态变化
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            console.log('Auth state change:', event, session);
            if (event === 'SIGNED_IN' && session?.user) {
                setUser(session.user);
            } else if (event === 'SIGNED_OUT') {
                setUser(null);
                setProfile(null);
            } else if (event === 'USER_UPDATED' && session?.user) {
                // 更新用户信息（如头像、姓名等）
                setUser(session.user);
            } else if (event === 'INITIAL_SESSION') {
                // 初始化会话，首次加载时触发
                if (session?.user) {
                    setUser(session.user);
                } else {
                    setUser(null);
                }
            } else if (event === 'PASSWORD_RECOVERY') {
                // 密码恢复事件，跳转到首页并打开 profile 设置页
                navigate('/?setting_modal=profile');
            }
        });

        // 清理订阅
        return () => subscription.unsubscribe();
    }, []);

    const { 
        workspaces, 
        currentWorkspace,
        isLoading: isLoadingWorkspaces, 
        refreshWorkspaces,
        switchWorkspace
    } = useWorkspaces(user?.id);

    const integrations = useWorkspaceIntegrations(currentWorkspace?.id);

    const workspaceMembers = useWorkspaceMembers(currentWorkspace?.id, user?.id, user?.email);
    const syncStatus = useSyncStatus(currentWorkspace?.id);

    // 获取当前用户的角色权限
    const permissions = usePermissions(profile?.workspace?.role as WorkspaceRole | null);

    const value = {
        openSignInModal,
        logout,
        user,
        profile,
        setProfile,
        userBilling,
        integrations,
        workspaceMembers,
        syncStatus,
        permissions,
        isLoadingProfile,
        workspaces,
        currentWorkspace,
        isLoadingWorkspaces,
        refreshWorkspaces,
        switchWorkspace,
        isGlobalLoading,
        showGlobalLoading,
        hideGlobalLoading
    };
    return (
        <AuthContext.Provider value={value}>
            <GlobalLoading isOpen={isGlobalLoading} />
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
